import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import PrivilegesContext from '../../../contexts/PrivilegesContext';
import { useGetLocalTimeForBusinessUnit } from '../../../hooks/useBusinessUnits';
import { client as publicClientApplication } from '../../../utilities/AuthUtils';
import { DateToLocaleString, longDateAndTimeFormat } from '../../../utilities/DateUtils';
import BusinessUnitField from './QIRFields/BusinessUnitField';
import ProductionLineField from './QIRFields/ProductionLineField';
import QIRStatusField from './QIRFields/QIRStatusField';

interface QIRSummaryProps {
    action: "view" | "update" | "create",
    qir: any,
    setQir: Dispatch<SetStateAction<any>>,
    businessUnitDetails: any,
    onChange: any,
    formDisabled: any,
    setFormDisabled: Dispatch<SetStateAction<any>>,
}

function QIRSummary({ action, qir, setQir, businessUnitDetails, onChange, formDisabled, setFormDisabled }: QIRSummaryProps) {
    const { t } = useTranslation();
    const account = publicClientApplication.getAllAccounts()[0];
    const { admin } = useContext(PrivilegesContext);
    const canEditStatement = admin || account.name === qir.originator || account.username === qir.originator;

    const defaultIncidentDateTime = useGetLocalTimeForBusinessUnit(qir.businessUnitId);
    qir.incidentDate = qir.incidentDate ?? defaultIncidentDateTime;

    useEffect(() => {
        let shouldBeDisabled = false;
        if (action === "view") {
            shouldBeDisabled = true;
        }

        if (action === "update") {
            let selectedStatus = businessUnitDetails?.statuses.filter((x: any) => x.id === +qir.statusId)[0];
            // A qir will always have a status so if it's undefined it means we're still loading it so disable the form
            if (selectedStatus === undefined) {
                shouldBeDisabled = true;
            }
            if (selectedStatus && selectedStatus.stateName === "Closed") {
                shouldBeDisabled = true;
            }
            if (selectedStatus && selectedStatus.stateName === "Cancelled") {
                shouldBeDisabled = true;
            }
        }

        if (qir.isDeleted) {
            shouldBeDisabled = true;
        }

        setFormDisabled(shouldBeDisabled);
    }, [action, qir?.statusId, qir?.isDeleted, businessUnitDetails?.statuses, setFormDisabled]);

    return (
        <>
            <Row>
                <Col md={6}>
                    <Form.Group as={Row} className="mb-1" controlId="qirId">
                        <Form.Label column sm={3}>{t('QIRDetail.Fields.Id')}</Form.Label>
                        <Col sm={9}>
                            <Form.Control type="text" size="sm" placeholder="<TBD>" value={qir.id ?? ""} name="qirId" onChange={onChange} readOnly />
                        </Col>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <QIRStatusField action={action} qir={qir} setQir={setQir} businessUnitDetails={businessUnitDetails} onChange={onChange} formDisabled={formDisabled} />
                </Col>
            </Row>
            <fieldset id="qir-summary-fieldset" disabled={formDisabled}>
                <Row>
                    <Col md={6}>
                        <BusinessUnitField action={action} qir={qir} setQir={setQir} onChange={onChange} />
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="incidentDate">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.IncidentDate')}</Form.Label>
                            <Col sm={9}>
                                {(action === "create" || action === "update") &&
                                    <Form.Control type="datetime-local" size="sm" value={qir.incidentDate ?? ""} name="incidentDate" onChange={onChange} />
                                }
                                {action === "view" &&
                                    <Form.Control type="text" size="sm" value={qir.incidentDate ? DateToLocaleString(qir.incidentDate, longDateAndTimeFormat) : ""} name="incidentDate" onChange={onChange} readOnly />
                                }
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <ProductionLineField action={action} qir={qir} setQir={setQir} businessUnitDetails={businessUnitDetails} onChange={onChange} />
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Row} className="mb-1" controlId="endDate">
                            <Form.Label column sm={3}>{t('QIRDetail.Fields.EndDate')}</Form.Label>
                            <Col sm={9}>
                                {(action === "create" || action === "update") &&
                                    <Form.Control type="datetime-local" size="sm" value={qir.endDate ?? ""} name="endDate" onChange={onChange} />
                                }
                                {action === "view" &&
                                    <Form.Control type="text" size="sm" value={qir.endDate ? DateToLocaleString(qir.endDate, longDateAndTimeFormat) : ""} name="endDate" onChange={onChange} readOnly />
                                }
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <hr />
                <Form.Group as={Row} className="mb-3" controlId="statement">
                    <Form.Label column sm={2} className="required">{t('QIRDetail.Fields.Statement')}</Form.Label>
                    <Col sm={10}>
                        <Form.Control as="textarea" required size="sm" rows={3} value={qir.statement ?? ""} name="statement" onChange={onChange} readOnly={!canEditStatement} />
                    </Col>
                </Form.Group>
            </fieldset>
        </>
    );
}

export default QIRSummary;